import { useCallback, useMemo, useState } from "react";
import { useLoaderData } from "@remix-run/react";

import { useRouteAction } from "~/hooks/useRouteAction";
import { useLabels } from "~/hooks/useLabels";
import { useDataRefresh } from "~/hooks/useDataRefresh";
import { track } from "~/utils/track";
import { getArtworkFromData } from "~/utils/data";
import { usePageState } from "~/hooks/usePageState";
import { pathFor, urlFor } from "~/utils/url";
import { Button } from "~/components/button/Button";
import { ButtonStack } from "~/components/button/ButtonStack";
import { ContentHero } from "~/components/contentHero/ContentHero";
import { Text } from "~/components/text/Text";
import { TabLink, TabNavigation, TabOutlet } from "~/components/tabs/Tabs";
import { IndexView } from "~/components/indexView/IndexView";
import { ShareModal } from "~/components/shareModal/ShareModal";
import { sendMessageToHost } from "~/utils/events";
import { sharePage } from "~/utils/share";
import { RouteType, Action, HostContext } from "~/constants";
import { showShareButton } from "~/utils/features";
import { useUserContext } from "~/hooks/useUserContext";
import { isMobileNative } from "~/utils/helpers";

import type { SyntheticEvent } from "react";
import type { ContentHeroProps } from "~/components/contentHero/ContentHero";
import type { ClassroomImage } from "~/graphql/types";
import type { ButtonProps } from "~/components/button/Button";
import type { ActionProp } from "~/components/components";
import type { TrackEventData } from "~/utils/track";
import type { ModuleRouteLoaderData } from "./route";

export function ModuleDetail() {
  const data = useLoaderData<ModuleRouteLoaderData>();
  const { fetcher } = useRouteAction();
  const { t } = useLabels();
  const { hostContext } = useUserContext();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const shareUrl = urlFor(RouteType.MODULE, {
    class: data.module.class?.slug!,
    module: String(data.module.position),
  });

  const trackingData = useMemo<TrackEventData>(
    () => ({
      className: data.module.class?.slug,
      moduleNumber: data.module.position,
      trigger: "moduleDetailHero",
    }),
    [data.module.class?.slug, data.module.position],
  );

  const getTabLink = useCallback(
    (tab: string) => {
      return pathFor(RouteType.MODULE_TAB, {
        class: data.module.class?.slug!,
        module: String(data.module.position),
        tab,
      });
    },
    [data.module.class?.slug, data.module.position],
  );

  const getBreadCrumbs = useCallback<
    () => ContentHeroProps["breadcrumbs"]
  >(() => {
    return [
      {
        text: t("common.classroom"),
        href: pathFor(RouteType.HOME),
        nativeBehavior: "pop",
      },
      {
        href: pathFor(RouteType.CLASS, {
          class: data.module.class?.slug!,
        }),
        text: data.module.class?.title!,
        nativeBehavior: "pop",
      },
    ];
  }, [data.module.class?.slug, data.module.class?.title, t]);

  const getHeroActions = useCallback<(isBookmarked: boolean) => ActionProp[]>(
    (_isBookmarked) => {
      return showShareButton()
        ? [
            {
              icon: "arrow-up-from-square",
              text: t("common.cta.share"),
              onClick: (e: SyntheticEvent) => {
                if (isMobileNative()) {
                  sharePage(shareUrl, e.target);
                } else {
                  setIsShareModalOpen(true);
                }

                track("crModuleshare", trackingData);
              },
            },
          ]
        : [];
    },
    [shareUrl, t, trackingData],
  );

  const renderedHeroButtons = useMemo(() => {
    const { currentContent } = data;

    const baseProps: Partial<ButtonProps> = {
      appearance: "primary",
      nativeBehavior: "presentFullScreen",
      nativeStencil: {
        propsJSON: "{}",
        type: "session",
      },
      onClick() {
        sendMessageToHost({
          ui: {
            haptic: {
              type: "notificationSuccess",
            },
          },
        });

        if (currentContent.state === "rewatch") {
          track("crModuleRewatch", trackingData);
          return;
        }

        if (currentContent.data.__typename === "Session") {
          track(
            currentContent.state === "start"
              ? "crSessionStart"
              : "crSessionResume",
            {
              ...trackingData,
              sessionNumber: currentContent.data.position,
            },
          );
          return;
        }

        if (currentContent.data.__typename === "ClassroomQuiz") {
          track(
            currentContent.state === "start" ? "crQuizStart" : "crQuizResume",
            trackingData,
          );
          return;
        }
      },
    };

    // Rewatch
    if (data.currentContent.state === "rewatch") {
      return (
        <Button
          {...baseProps}
          icon="arrow-rotate-left"
          href={pathFor(RouteType.SESSION, {
            class: data.module.class?.slug!,
            session: String(data.module.sessions?.nodes[0].position),
          })}
        >
          {t("module.cta.rewatch")}
        </Button>
      );
    }

    // Quiz
    if (data.currentContent.data.__typename === "ClassroomQuiz") {
      return (
        <Button
          {...baseProps}
          href={pathFor(RouteType.QUIZ, {
            class: data.module.class?.slug!,
            quiz: String(currentContent.data.position),
          })}
        >
          {data.currentContent.state === "resume"
            ? t("common.cta.resumeQuiz", {
                number: currentContent.data.position,
              })
            : t("common.cta.startQuiz", {
                number: currentContent.data.position,
              })}
        </Button>
      );
    }

    // Session
    return (
      <Button
        {...baseProps}
        href={pathFor(RouteType.SESSION, {
          class: data.module.class?.slug!,
          session: String(currentContent.data.position),
        })}
        icon="play"
        iconVariant="solid"
      >
        {data.currentContent.state === "resume"
          ? t("common.cta.resumeSession", {
              number: currentContent.data.position,
            })
          : t("common.cta.startSession", {
              number: currentContent.data.position,
            })}
      </Button>
    );
  }, [data, t, trackingData]);

  const renderHero = useCallback(() => {
    const intent = fetcher.formData?.get("intent");

    const isBookmarked = intent
      ? intent === Action.BookmarkModule
      : Boolean(data.module.user?.isBookmarked);

    const progress = data.module.user?.isComplete
      ? 1
      : (data.module.user?.progress ?? 0);

    function handleNotebookClick() {
      track("crViewNotebook", trackingData);
    }

    return (
      <ContentHero
        actions={getHeroActions(isBookmarked)}
        artwork={
          getArtworkFromData(data.module.artwork as ClassroomImage[], "16:9")
            ?.url
        }
        artworkColor={data.module.class?.color.primary}
        breadcrumbs={getBreadCrumbs()}
        description={data.module.description!}
        duration={data.module.duration!}
        notebookHref={pathFor(RouteType.NOTEBOOK, {
          class: data.module.class?.slug!,
        })}
        progress={progress}
        preTitle={t("module.preTitle", { number: data.module.position })}
        postTitle={
          <>
            <Text>{data.module.scripture}</Text>
          </>
        }
        title={data.module.title}
        variant={
          hostContext === HostContext.MobileNative ? "mobile-native" : "default"
        }
        onNotebookClick={handleNotebookClick}
      >
        <ButtonStack>{renderedHeroButtons}</ButtonStack>
      </ContentHero>
    );
  }, [
    data.module.artwork,
    data.module.class?.color.primary,
    data.module.class?.slug,
    data.module.description,
    data.module.duration,
    data.module.position,
    data.module.scripture,
    data.module.title,
    data.module.user?.isBookmarked,
    data.module.user?.isComplete,
    data.module.user?.progress,
    fetcher.formData,
    getBreadCrumbs,
    getHeroActions,
    hostContext,
    renderedHeroButtons,
    t,
    trackingData,
  ]);

  const renderTabs = useCallback(() => {
    return (
      <TabNavigation>
        <TabLink href={getTabLink("")} isSelected>
          {t("module.tab.sessions")}
        </TabLink>
      </TabNavigation>
    );
  }, [getTabLink, t]);

  const renderBody = useCallback(() => <TabOutlet />, []);

  const renderShareModal = useCallback(() => {
    return (
      <ShareModal
        isOpen={isShareModalOpen}
        shareUrl={shareUrl}
        onRequestClose={() => setIsShareModalOpen(false)}
      />
    );
  }, [isShareModalOpen, shareUrl]);

  useDataRefresh();
  usePageState({ pageType: "module" });

  return (
    <>
      <IndexView>
        <IndexView.Hero>{renderHero()}</IndexView.Hero>

        <IndexView.Body>
          {renderTabs()}
          {renderBody()}
        </IndexView.Body>
      </IndexView>

      {renderShareModal()}
    </>
  );
}
